import React, { useState, useEffect } from 'react';
import { Modal, DefaultButton, PrimaryButton } from '@fluentui/react';

function ClientProposalModal({
  files,
  isOpen,
  setIsOpen,
  onUpload,
}: {
  files: File[];
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  onUpload: (
    filesWithJobDescriptions: {
      file: File;
      organisation: string;
      roleTitle: string;
      clientContact: string;
      contactTitle: string;
      address: string;
      suburb: string;
      state: string;
      postCode: string;
      totalRemuneration: string;
      roleType: string;
      sector: string;
      assignmentDate: string;
      candidateSearch: string;
      interviews: string;
      referencing: string;
      noticePeriod: string;
      startDate: string;
      replacementGuarantee: string;
    }[],
  ) => Promise<void>;
}) {
  const [organisation, setOrganisation] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [clientContact, setClientContact] = useState('');
  const [contactTitle, setContactTitle] = useState('');
  const [address, setAddress] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postCode, setPostCode] = useState('');
  const [totalRemuneration, setTotalRemuneration] = useState('');
  const [roleType, setRoleType] = useState('');
  const [sector, setSector] = useState('');
  const [assignmentDate, setAssignmentDate] = useState('');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [interviews, setInterviews] = useState('');
  const [referencing, setReferencing] = useState('');
  const [noticePeriod, setNoticePeriod] = useState('');
  const [startDate, setStartDate] = useState('');
  const [replacementGuarantee, setReplacementGuarantee] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (files.length <= 1) {
        setIsOpen(false);
        alert('Please upload at least two documents to proceed.');
      } else if (files.length > 3) {
        setIsOpen(false);
        alert('You should have no more than 2 client bios uploaded to proceed.');
      }
    }
  }, [files, isOpen, setIsOpen]);

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    setter(value);
  };

  const handleSubmit = () => {
    const filesWithJobDescriptions = files.map(file => ({
      file,
      organisation,
      roleTitle,
      clientContact,
      contactTitle,
      address,
      suburb,
      state,
      postCode,
      totalRemuneration,
      roleType,
      sector,
      assignmentDate,
      candidateSearch,
      interviews,
      referencing,
      noticePeriod,
      startDate,
      replacementGuarantee,
    }));
    onUpload(filesWithJobDescriptions);
    setIsOpen(false);
    // Clear the fields
    setOrganisation('');
    setRoleTitle('');
    setClientContact('');
    setContactTitle('');
    setAddress('');
    setSuburb('');
    setState('');
    setPostCode('');
    setTotalRemuneration('');
    setRoleType('');
    setSector('');
    setAssignmentDate('');
    setCandidateSearch('');
    setInterviews('');
    setReferencing('');
    setNoticePeriod('');
    setStartDate('');
    setReplacementGuarantee('');
  };

  return (
    <Modal isOpen={isOpen} onDismiss={() => setIsOpen(false)} isBlocking={false}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem',
          borderBottom: '1px solid #ccc',
        }}
      >
        <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{files[0]?.name || 'No file selected'}</span>
        <DefaultButton onClick={() => setIsOpen(false)} iconProps={{ iconName: 'Cancel' }} />
      </div>
      <div style={{ padding: '1rem', width: '40rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <h3>General inputs required for the proposal</h3>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Organisation:</label>
            <input
              type="text"
              value={organisation}
              onChange={e => handleInputChange(setOrganisation, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Role Title:</label>
            <input
              type="text"
              value={roleTitle}
              onChange={e => handleInputChange(setRoleTitle, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Client Contact:</label>
            <input
              type="text"
              value={clientContact}
              onChange={e => handleInputChange(setClientContact, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Contact Title:</label>
            <input
              type="text"
              value={contactTitle}
              onChange={e => handleInputChange(setContactTitle, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Address:</label>
            <input
              type="text"
              value={address}
              onChange={e => handleInputChange(setAddress, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Suburb:</label>
            <input
              type="text"
              value={suburb}
              onChange={e => handleInputChange(setSuburb, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>State:</label>
            <input
              type="text"
              value={state}
              onChange={e => handleInputChange(setState, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Post Code:</label>
            <input
              type="text"
              value={postCode}
              onChange={e => handleInputChange(setPostCode, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Total remuneration of role $XXX:</label>
            <input
              type="text"
              value={totalRemuneration}
              onChange={e => handleInputChange(setTotalRemuneration, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <h3>Understanding of the needs</h3>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Role Type:</label>
            <input
              type="text"
              value={roleType}
              onChange={e => handleInputChange(setRoleType, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Sector:</label>
            <input
              type="text"
              value={sector}
              onChange={e => handleInputChange(setSector, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <h3>Sourcing and timeline</h3>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Confirmation of assignment date:</label>
            <input
              type="text"
              value={assignmentDate}
              onChange={e => handleInputChange(setAssignmentDate, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Candidate Search:</label>
            <input
              type="text"
              value={candidateSearch}
              onChange={e => handleInputChange(setCandidateSearch, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Interviews with short listed candidates:</label>
            <input
              type="text"
              value={interviews}
              onChange={e => handleInputChange(setInterviews, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              Referencing, probity and offer management:
            </label>
            <input
              type="text"
              value={referencing}
              onChange={e => handleInputChange(setReferencing, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Preferred candidate's notice period:</label>
            <input
              type="text"
              value={noticePeriod}
              onChange={e => handleInputChange(setNoticePeriod, e.target.value)}
              placeholder="Please enter a notice period duration, not a range."
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              Preferred candidate date to begin work in their new position:
            </label>
            <input
              type="text"
              value={startDate}
              onChange={e => handleInputChange(setStartDate, e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <h3>Replacement Guarantee</h3>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>Replacement guarantee period:</label>
          <input
            type="text"
            value={replacementGuarantee}
            onChange={e => handleInputChange(setReplacementGuarantee, e.target.value)}
            placeholder="Enter number to indicate amount of months (period to re-conduct the assignment)"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem', borderTop: '1px solid #ccc' }}>
        <PrimaryButton onClick={handleSubmit} text="Submit" />
        <DefaultButton onClick={() => setIsOpen(false)} text="Cancel" />
      </div>
    </Modal>
  );
}

export default ClientProposalModal;
